import React, { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogContent, DialogTitle, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Grid, Switch, FormControlLabel } from '@mui/material';
import { Close } from '@mui/icons-material';
import { headingBlue, red, lightGrey, mediumGrey } from '../../../assets/colors';
import { buttonStyle } from '../../../utils/commonCSS';
import { useDispatch } from 'react-redux';
import { showSnackBar } from '../../CustomComponents/CustomComponentsSlice';
import Loader from '../../Loader';
import HttpService from '../../../backend/requestApis';
import moment from 'moment';
import { fetchClassAttendance, fetchCoordinatorClassAttendance, updateCoordinatorClassAttendance } from '../../../backend/backendApis';

const AttendanceDialog = (props) => {

    const { dialogStatus, updateFunc, classId, classDetails, isCoordinator } = props;
    const dispatch = useDispatch();

    const [attendees, setAttendees] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const isAttendanceDisabled = classDetails?.status?.toLowerCase() !== 'ongoing';

    const titleStyle = {
        backgroundColor: headingBlue, 
        color: 'white',
        fontSize: '1rem',
        display: "flex",
        alignItems: 'center',
        padding: "0.4rem 1rem",
        justifyContent: 'space-between'
    };

    const headerCellStyle = {
        padding: '0.4rem 0.5rem', 
        fontFamily: "Helvetica",
        fontSize: "0.9rem", 
        fontWeight: "600",
        textAlign: 'center',
        backgroundColor: lightGrey,
        borderBottom: `1px solid ${mediumGrey}`
    };

    const bodyCellStyle = {
        padding: '0.4rem 0.5rem', 
        fontFamily: "Helvetica",
        fontSize: "0.9rem", 
        fontWeight: "400",
        textAlign: 'center',
    };

    const handleStatusChange = (index, newStatus) => {
        const updatedAttendees = [...attendees];
        updatedAttendees[index].status = newStatus;
        setAttendees(updatedAttendees);
    };

    const handleSave = () => {
        setIsLoading(true);

        const attendanceData = attendees.map(attendee => ({
            memberId: attendee._id || attendee.memberId || attendee.id,
            status: attendee.status ? attendee.status.toLowerCase() : 'unmarked' // Ensure status is lowercase
        }));
        
        HttpService({
            url: updateCoordinatorClassAttendance,
            method: 'POST',
            data: { classId, attendanceData }, // Updated to match the payload
            headers: {headers: { Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null }} // Fixed headers typo
        }).then(resp => {
            setIsLoading(false);
            dispatch(showSnackBar({ message: 'Attendance saved successfully.', severity: 'success' }));
            updateFunc(false);
        }).catch(err => {
            console.error("Error saving attendance: ", err);
            setIsLoading(false);
            dispatch(showSnackBar({ message: 'Error saving attendance.', severity: 'error' }));
        });
    };

    useEffect(() => {
        if (dialogStatus && classId) {
            setIsLoading(true);
            let options = {
                url: isCoordinator ? fetchCoordinatorClassAttendance : fetchClassAttendance,
                method: 'GET',
                params: { classId: classId },
                headers: {
                    Authorization: localStorage.getItem('token') ? `Bearer ${localStorage.getItem('token')}` : null
                }
            };

            HttpService(options)
                .then(resp => {
                    setAttendees(resp?.data?.data || []);
                    setIsLoading(false);
                })
                .catch(err => {
                    console.error("Error fetching attendance: ", err);
                    dispatch(showSnackBar({ message: 'Error fetching attendance.', severity: 'error' }));
                    setIsLoading(false);
                });
        }
    }, [dialogStatus, classId]);

    return (
        <Dialog maxWidth='md' fullWidth open={dialogStatus} onClose={() => updateFunc(false)}>
            <DialogTitle style={titleStyle}>
                <Typography style={{ fontWeight: 600 }}>Class Attendance</Typography>
                <IconButton onClick={() => updateFunc(false)} style={{ color: 'white', padding: 0 }}>
                    <Close />
                </IconButton>
            </DialogTitle>
             <DialogContent>
                {isLoading && <Loader />}
                <Box style={{ padding: '1.5rem 0rem 0rem 0rem' }}>
                    {classDetails && (
                        <Box style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f9f9f9', borderRadius: '0.4rem', border: `1px solid ${mediumGrey}` }}>
                            <Grid container spacing={2} justifyContent="space-between">
                                <Grid item xs={3} style={{ wordBreak: 'break-word' }}>
                                    <Typography variant="body2" style={{ color: '#888' }}>Program Name</Typography>
                                    <Typography variant="body1" style={{ fontWeight: 600, color: headingBlue }}>{classDetails.programName || '-'}</Typography>
                                </Grid>
                                <Grid item xs={3} style={{ wordBreak: 'break-word' }}>
                                    <Typography variant="body2" style={{ color: '#888' }}>Batch Name</Typography>
                                    <Typography variant="body1" style={{ fontWeight: 600, color: headingBlue }}>{classDetails.batchName || '-'}</Typography>
                                </Grid>
                                <Grid item xs={3} style={{ wordBreak: 'break-word' }}>
                                    <Typography variant="body2" style={{ color: '#888' }}>Class Date</Typography>
                                    <Typography variant="body1" style={{ fontWeight: 600, color: headingBlue }}>{classDetails.classDate ? moment(classDetails.classDate).utcOffset('+05:30').format('DD/MM/YYYY') : '-'}</Typography>
                                </Grid>
                                <Grid item xs={3} style={{ wordBreak: 'break-word' }}>
                                    <Typography variant="body2" style={{ color: '#888' }}>Coordinator</Typography>
                                    <Typography variant="body1" style={{ fontWeight: 600, color: headingBlue }}>
                                        {classDetails.coordinatorName || classDetails.volunteerName || '-'}
                                        {(classDetails.coordinatorNumber || classDetails.volunteerCN) && (
                                            <span style={{ fontSize: '0.85rem', color: '#666', fontWeight: 400, marginLeft: '0.4rem' }}>
                                                - {classDetails.coordinatorNumber || classDetails.volunteerCN}
                                            </span>
                                        )}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                    
                    <TableContainer component={Paper} style={{ border: `1px solid ${mediumGrey}`, boxShadow: 'none', maxHeight: '55vh', overflowY: 'auto' }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell style={headerCellStyle}>Name</TableCell>
                                    <TableCell style={headerCellStyle}>Contact</TableCell>
                                    <TableCell style={headerCellStyle}>State</TableCell>
                                    <TableCell style={headerCellStyle}>District</TableCell>
                                    <TableCell style={headerCellStyle}>Attendance Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(isLoading || attendees.length === 0) ? (
                                    <TableRow>
                                        <TableCell colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                                            {!isLoading && "No participants found."}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    attendees.map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell style={bodyCellStyle}>{row.name}</TableCell>
                                            <TableCell style={bodyCellStyle}>{row.contact}</TableCell>
                                            <TableCell style={bodyCellStyle}>{row.state || '-'}</TableCell>
                                            <TableCell style={bodyCellStyle}>{row.district || '-'}</TableCell>
                                            <TableCell style={bodyCellStyle}>
                                                {isCoordinator ? (
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={row.status === 'Present'}
                                                                onChange={(e) => handleStatusChange(index, e.target.checked ? 'Present' : 'Absent')}
                                                                color="success"
                                                                size="small"
                                                                disabled={isAttendanceDisabled}
                                                            />
                                                        }
                                                        label={
                                                            <Typography variant="body2" style={{ fontWeight: 600, color: row.status === 'Present' ? 'green' : row.status === 'Absent' ? red : '#888' }}>
                                                                {row.status || 'Unmarked'}
                                                            </Typography>
                                                        }
                                                        style={{ margin: 0 }}
                                                    />
                                                ) : (
                                                    <Typography variant="body2" style={{ fontWeight: 600, color: row.status === 'Present' ? 'green' : row.status === 'Absent' ? red : 'inherit' }}>
                                                        {row.status || '-'}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Box style={{ padding: '1rem 0rem 0rem 0rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                        {isCoordinator && (
                            <Button style={{ ...buttonStyle, backgroundColor: (isLoading || isAttendanceDisabled) ? mediumGrey : headingBlue, padding: '0.3rem 1.5rem' }} onClick={handleSave} disabled={isLoading || isAttendanceDisabled}>
                                Save
                            </Button>
                        )}
                        <Button style={{ ...buttonStyle, backgroundColor: red, padding: '0.3rem 1.5rem' }} onClick={() => updateFunc(false)}>
                            Close
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default AttendanceDialog;